/**
 * CostPreview — Dialog for cost confirmation before expensive operations.
 *
 * Shows cost estimate, row count, duration estimate.
 * Triggered by `cost_approval_required` SSE event when estimated cost > $0.50.
 */
import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AttachMoney as CostIcon,
  Speed as SpeedIcon,
  Storage as RowsIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';

interface CostPreviewProps {
  open: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  operationType: string;
  description: string;
  estimatedCostUsd: number;
  rowCount?: number;
  estimatedDurationSec?: number;
}

/** Key-value detail row. */
const DetailRow: React.FC<{ icon: React.ReactElement; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75 }}>
    <Box sx={{ color: 'text.secondary' }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

const CostPreview: React.FC<CostPreviewProps> = ({
  open,
  onClose,
  onApprove,
  onReject,
  operationType,
  description,
  estimatedCostUsd,
  rowCount,
  estimatedDurationSec,
}) => {
  const theme = useTheme();
  const isExpensive = estimatedCostUsd >= 1.0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CostIcon sx={{ color: isExpensive ? 'warning.main' : 'info.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Cost Confirmation
          </Typography>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* Operation description */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.04),
            border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
            mb: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.04em' }}>
            {operationType.replace(/_/g, ' ')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        </Box>

        {/* Cost details */}
        <DetailRow
          icon={<CostIcon sx={{ fontSize: 20 }} />}
          label="Estimated Cost"
          value={`$${estimatedCostUsd.toFixed(4)}`}
        />
        {rowCount != null && (
          <DetailRow
            icon={<RowsIcon sx={{ fontSize: 20 }} />}
            label="Rows to Process"
            value={rowCount.toLocaleString()}
          />
        )}
        {estimatedDurationSec != null && (
          <DetailRow
            icon={<SpeedIcon sx={{ fontSize: 20 }} />}
            label="Estimated Duration"
            value={
              estimatedDurationSec >= 60
                ? `~${Math.ceil(estimatedDurationSec / 60)} min`
                : `~${estimatedDurationSec}s`
            }
          />
        )}

        {/* Warning for expensive operations */}
        {isExpensive && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.06),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <WarnIcon sx={{ fontSize: 18, color: 'warning.main', mt: 0.25 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              This operation has a higher cost estimate. Consider reducing the row count
              or using a simpler approach if cost is a concern.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onReject} color="inherit" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onApprove}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Approve &amp; Run
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CostPreview;
